import React, { useState, useRef, useCallback, useEffect } from 'react';
import CoachingPanel from './CoachingPanel';

const SWIPE_THRESHOLD_PX = 50;
const WHEEL_THRESHOLD = 40;

function EvidenceChecklist({ plan, ticket, triageResult, isOnline, onSubmit, loading }) {
  // Default plan if none provided (offline fallback)
  const steps = plan?.steps || [
    { step_number: 1, action: 'Perform visual safety inspection.', expected_normal: 'No obvious hazards.', record_instruction: 'Note any safety concerns.', safety_note: 'Ensure equipment is powered down.', required: true },
    { step_number: 2, action: 'Verify equipment matches ticket information.', expected_normal: 'Model and hours match.', record_instruction: 'Confirm details.', safety_note: null, required: true },
    { step_number: 3, action: 'Document all active fault indicators.', expected_normal: 'Matches reported symptoms.', record_instruction: 'Record all codes and warnings.', safety_note: null, required: true },
    { step_number: 4, action: 'Photograph equipment and visible issues.', expected_normal: 'N/A', record_instruction: 'Take 3-5 photos.', safety_note: null, required: true },
  ];

  const [completedSteps, setCompletedSteps] = useState({});
  const [stepValues, setStepValues] = useState({});
  const [stepPhotos, setStepPhotos] = useState({}); // { [step_number]: base64[] }
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [activeCoaching, setActiveCoaching] = useState(null);
  const carouselRef = useRef(null);
  const touchStartX = useRef(0);
  const wheelLockRef = useRef(false);
  const inputRefs = useRef({});
  const photoInputRefs = useRef({});

  const goTo = useCallback((index) => {
    setCurrentStepIndex((i) => Math.max(0, Math.min(steps.length - 1, index)));
  }, [steps.length]);

  const goNext = useCallback(() => {
    setCurrentStepIndex((i) => (i < steps.length - 1 ? i + 1 : i));
  }, [steps.length]);
  const goPrev = useCallback(() => {
    setCurrentStepIndex((i) => (i > 0 ? i - 1 : i));
  }, []);

  const toggleStep = (stepNumber) => {
    setCompletedSteps((prev) => ({
      ...prev,
      [stepNumber]: !prev[stepNumber],
    }));
  };

  const handleValueChange = (stepNumber, value) => {
    setStepValues((prev) => ({ ...prev, [stepNumber]: value }));
  };

  const handleStepPhotoChange = (stepNumber, e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const max = 5;
    const imageFiles = files.filter((f) => f.type.startsWith('image/')).slice(0, max);
    let loaded = 0;
    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setStepPhotos((prev) => {
          const list = prev[stepNumber] || [];
          const next = [...list, reader.result].slice(0, max);
          return { ...prev, [stepNumber]: next };
        });
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeStepPhoto = (stepNumber, index) => {
    setStepPhotos((prev) => {
      const list = (prev[stepNumber] || []).filter((_, i) => i !== index);
      return { ...prev, [stepNumber]: list };
    });
  };

  const handleCompleteStep = (stepNumber) => {
    setCompletedSteps((prev) => ({ ...prev, [stepNumber]: true }));
    const idx = steps.findIndex((s) => s.step_number === stepNumber);
    if (idx >= 0 && idx < steps.length - 1) {
      goTo(idx + 1);
    }
  };

  const handleWheel = useCallback((e) => {
    if (!carouselRef.current || steps.length <= 1 || wheelLockRef.current) return;
    if (e.deltaY > WHEEL_THRESHOLD) {
      e.preventDefault();
      wheelLockRef.current = true;
      setCurrentStepIndex((i) => (i < steps.length - 1 ? i + 1 : i));
      setTimeout(() => { wheelLockRef.current = false; }, 500);
    } else if (e.deltaY < -WHEEL_THRESHOLD) {
      e.preventDefault();
      wheelLockRef.current = true;
      setCurrentStepIndex((i) => (i > 0 ? i - 1 : i));
      setTimeout(() => { wheelLockRef.current = false; }, 500);
    }
  }, [steps.length]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const endX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - endX;
    if (Math.abs(diff) < SWIPE_THRESHOLD_PX) return;
    if (diff > 0) goNext();
    else goPrev();
  };

  const handleCoachMe = (step) => {
    if (activeCoaching === step.step_number) {
      setActiveCoaching(null);
      return;
    }
    setActiveCoaching(step.step_number);
  };

  const allRequiredComplete = steps
    .filter((s) => s.required)
    .every((s) => completedSteps[s.step_number]);

  const handleSubmit = () => {
    const results = steps.map((step) => {
      const photos = stepPhotos[step.step_number] || [];
      return {
        step_number: step.step_number,
        action: step.action,
        result_value: stepValues[step.step_number] || null,
        result_notes: null,
        completed: !!completedSteps[step.step_number],
        photo_attached: photos.length > 0,
        photo_base64_list: photos.length > 0 ? photos : null,
      };
    });
    onSubmit(results);
  };

  const completedCount = Object.values(completedSteps).filter(Boolean).length;
  const isLastStep = currentStepIndex === steps.length - 1;

  return (
    <div className="evidence-page">
      <h2 className="evidence-page__title">Evidence Collection</h2>
      <p className="evidence-page__subtitle">
        Complete each step and record your findings
      </p>

      <div className="evidence-page__progress">
        <span className="evidence-page__progress-step">
          Step {currentStepIndex + 1} of {steps.length}
        </span>
        <span className="evidence-page__progress-done">
          {completedCount} of {steps.length} steps completed
        </span>
        {plan?.estimated_time_minutes && (
          <span className="evidence-page__progress-time">
            · ~{plan.estimated_time_minutes} min estimated
          </span>
        )}
      </div>

      {plan?.equipment_needed && plan.equipment_needed.length > 0 && (
        <div className="evidence-page__equipment">
          <strong>Equipment needed:</strong> {plan.equipment_needed.join(', ')}
        </div>
      )}

      {/* Step progress dots */}
      <div className="evidence-page__steps-bar" aria-hidden="true">
        {steps.map((step, idx) => (
          <button
            key={step.step_number}
            type="button"
            className={`evidence-page__step-dot ${idx === currentStepIndex ? 'evidence-page__step-dot--current' : ''} ${completedSteps[step.step_number] ? 'evidence-page__step-dot--done' : ''}`}
            onClick={() => goTo(idx)}
            aria-label={`Go to step ${step.step_number}`}
            title={step.action}
          >
            {completedSteps[step.step_number] ? '✓' : step.step_number}
          </button>
        ))}
      </div>

      {/* Carousel: swipe left/right or scroll to slide — stage has shadow, card lowered */}
      <div className="evidence-step__stage">
        <div
          ref={carouselRef}
          className="evidence-step__carousel"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          style={{ touchAction: 'pan-y' }}
        >
          <div
            className="evidence-step__track"
            style={{
              width: `${steps.length * 100}%`,
              transform: `translateX(-${currentStepIndex * (100 / steps.length)}%)`,
            }}
          >
            {steps.map((step) => (
              <div key={step.step_number} className="evidence-step__slide" style={{ width: `${100 / steps.length}%` }}>
                <div className={`checklist-step evidence-step__card ${completedSteps[step.step_number] ? 'checklist-step--completed' : ''}`}>
                  <div className="checklist-step__header">
                    <div
                      className="checklist-step__number"
                      onClick={() => toggleStep(step.step_number)}
                      style={{ cursor: 'pointer' }}
                    >
                      {completedSteps[step.step_number] ? '✓' : step.step_number}
                    </div>
                    <div className="evidence-step__instruction-box">
                      <div className="checklist-step__action">{step.action}</div>
                    </div>
                  </div>

                  <div className="evidence-step__expected-box">
                    <div className="checklist-step__expected">
                      <strong>Normal:</strong> {step.expected_normal}
                    </div>
                  </div>

                  {step.safety_note && (
                    <div className="checklist-step__safety">
                      <span className="checklist-step__safety-icon" aria-hidden="true">⚠</span>
                      {step.safety_note}
                    </div>
                  )}

                  <div className="evidence-step__input-wrap evidence-step__record-box">
                  <p className="evidence-step__record-label" id={`record-${step.step_number}`}>
                    {step.record_instruction}
                  </p>
                  <input
                    ref={(el) => { inputRefs.current[step.step_number] = el; }}
                    className="form-input evidence-step__input"
                    placeholder="Enter your findings..."
                    value={stepValues[step.step_number] || ''}
                    onChange={(e) => handleValueChange(step.step_number, e.target.value)}
                    aria-labelledby={`record-${step.step_number}`}
                  />
                  <div className="form-group photo-upload">
                    <span className="form-label">Photos (optional)</span>
                    <div className="photo-upload__wrap">
                      <input
                        ref={(el) => { photoInputRefs.current[step.step_number] = el; }}
                        id={`photo-input-step-${step.step_number}`}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        multiple
                        onChange={(e) => handleStepPhotoChange(step.step_number, e)}
                        className="photo-upload__input photo-upload__input--hidden"
                        aria-label={`Attach photos for step ${step.step_number}`}
                      />
                      <label
                        htmlFor={`photo-input-step-${step.step_number}`}
                        className="photo-upload__btn"
                      >
                        Choose photos
                      </label>
                      {(stepPhotos[step.step_number] || []).length > 0 && (
                        <span className="photo-upload__hint">
                          {(stepPhotos[step.step_number] || []).length} selected
                        </span>
                      )}
                    </div>
                    {(stepPhotos[step.step_number] || []).length > 0 && (
                      <div className="photo-thumbnails">
                        {(stepPhotos[step.step_number] || []).map((dataUrl, i) => (
                          <div key={i} className="photo-thumbnail">
                            <img src={dataUrl} alt={`Step ${step.step_number} photo ${i + 1}`} className="photo-thumbnail__img" />
                            <button
                              type="button"
                              className="photo-thumbnail__remove"
                              onClick={() => removeStepPhoto(step.step_number, i)}
                              aria-label="Remove photo"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="evidence-step__actions">
                  <button
                    className="btn btn--coach btn--sm"
                    onClick={() => handleCoachMe(step)}
                  >
                    {activeCoaching === step.step_number ? 'Hide Coach' : '💡 Coach Me'}
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary evidence-step__complete-btn"
                    onClick={() => handleCompleteStep(step.step_number)}
                  >
                    {completedSteps[step.step_number] ? 'Step complete ✓' : 'Complete step'}
                  </button>
                </div>

                {completedSteps[step.step_number] && (
                  <div className="evidence-step__secondary-actions">
                    <button
                      type="button"
                      className="evidence-step__link-btn"
                      onClick={() => toggleStep(step.step_number)}
                      aria-label="Mark step incomplete"
                    >
                      Mark incomplete
                    </button>
                    <span className="evidence-step__secondary-sep" aria-hidden="true">·</span>
                    <button
                      type="button"
                      className="evidence-step__link-btn"
                      onClick={() => inputRefs.current[step.step_number]?.focus()}
                      aria-label="Edit your notes for this step"
                    >
                      Edit notes
                    </button>
                  </div>
                )}

                {activeCoaching === step.step_number && (
                  <CoachingPanel
                    ticketId={ticket?.ticket_id}
                    stepAction={step.action}
                    initialQuestion="How do I do this step?"
                    isOnline={isOnline}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>

      {/* Previous / Next when multiple steps */}
      {steps.length > 1 && (
        <div className="evidence-page__nav">
          <button
            type="button"
            className="btn btn--outline btn--sm"
            onClick={goPrev}
            disabled={currentStepIndex === 0}
          >
            Previous step
          </button>
          {!isLastStep && (
            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={goNext}
            >
              Next step
            </button>
          )}
        </div>
      )}

      <button
        className="btn btn--primary btn--block evidence-page__submit"
        onClick={handleSubmit}
        disabled={loading || !allRequiredComplete}
      >
        {loading
          ? 'Submitting evidence...'
          : allRequiredComplete
          ? 'Submit Evidence'
          : `Complete all required steps (${completedCount}/${steps.filter((s) => s.required).length})`}
      </button>
    </div>
  );
}

export default EvidenceChecklist;
